/**
 * Zustand chat muse store.
 */
import type { UploadFile } from 'antd';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { ImageCanvasEntity } from '@/pages/GenerationMedia/ai-chat-image/components/chatCanvas/types/imageCanvas';

export interface BrandItem {
  id: string;
  brand_name: string;
}

export type MuseMediaType = 'image' | 'video';

export interface MuseAssetDetail {
  prompt: string;
  prompt_show: string;
  aspect_ratio: string;
  resolution: string;
}

export interface MuseMediaAsset {
  [key: string]: any;
  isAssetRegistered?: boolean;
  type: string;
  parent_asset_ids: string[];
  group_id: string[];
  asset_id: string;
  url: string;
  http_url: string;
  thumbnail_url: string;
  thumbnail_http_url: string;
  input_mask_image?: string;
  asset_detail: MuseAssetDetail;
}

export interface MuseWorkbenchAssetItem {
  medio_asset: MuseMediaAsset[];
}

export interface MuseAnnotationStroke {
  points: number[];
  stroke: string;
  strokeWidth: number;
}

export interface MuseAnnotationText {
  id?: string;
  x: number;
  y: number;
  width?: number;
  text: string;
  fontSize: number;
  fill: string;
}

export interface MuseAnnotationData {
  strokes: MuseAnnotationStroke[];
  texts: MuseAnnotationText[];
}

export interface ChatInputMaskAsset {
  id: string;
  url: string;
  http_url: string;
  thumbnail_url: string;
  thumbnail_http_url: string;
}

export type ChatInputMediaFile = ImageCanvasEntity &
  Omit<UploadFile, 'type'> & {
    node_id?: string;
    input_mask_image?: string;
    mask_asset?: ChatInputMaskAsset;
  };

export type MuseModelGroupKey = 'image' | 'reel' | 'auto';

export type MuseModelItem = {
  id: string;
  show_name: string;
  config?: {
    aspect_ratios?: Array<{
      label: string;
      value: string;
    }>;
    default_ratio?: string;
    resolutions?: Array<{
      label: string;
      value: string;
    }>;
    default_resolution?: string;
  };
  description?: string;
};

export type MuseModelList = Record<MuseModelGroupKey, MuseModelItem[]>;
export type MuseQuickTab = 'optimize' | 'inspiration';
export type EntityCountSource = 'entity' | 'session-query' | null;
export type EntityModalSource = 'entity' | 'canvas' | null;

type ChatMuseState = {
  seessionId: string | null;
  museType: string | null;
  setSeessionId: (seessionId: string) => void;
  setMuseType: (museType: string) => void;
  chatIng: boolean;
  setChatIng: (chatIng: boolean) => void;
  selectBrand: BrandItem | null;
  setSelectBrand: (selectBrand: BrandItem) => void;
  imageGlobaldisabled: boolean;
  setImageGlobaldisabled: (imageGlobaldisabled: boolean) => void;
  entityCount: number;
  entityCountSource: EntityCountSource;
  setEntityCount: (entityCount: number, source?: Exclude<EntityCountSource, null>) => void;
  entityModalOpen: boolean;
  entityModalSource: EntityModalSource;
  setEntityModalOpen: (entityModalOpen: boolean, source?: Exclude<EntityModalSource, null>) => void;
  entityFormModalOpen: boolean;
  entityFormModalSource: EntityModalSource;
  setEntityFormModalOpen: (entityFormModalOpen: boolean, source?: Exclude<EntityModalSource, null>) => void;
  resetPageStatus: () => void;
  chatInputFileList: ChatInputMediaFile[];
  setChatInputFileList: (chatInputFileList: ChatInputMediaFile[] | ((prev: ChatInputMediaFile[]) => ChatInputMediaFile[])) => void;
  modelList: MuseModelList;
  setModelList: (modelList: MuseModelList) => void;
  museSelectedModel?: string;
  setMuseSelectedModel: (museSelectedModel?: string) => void;
  museSelectedSize?: string;
  setMuseSelectedSize: (museSelectedSize?: string) => void;
  museSelectedResolution?: string;
  setMuseSelectedResolution: (museSelectedResolution?: string) => void;
  museActiveQuickTab: MuseQuickTab[];
  setMuseActiveQuickTab: (museActiveQuickTab: MuseQuickTab[]) => void;
  canvasId: string | null;
  setCanvasId: (canvasId: string | null) => void;
  museHistorySessionChanged: boolean;
  markMuseHistorySessionChanged: () => void;
  resetMuseHistorySessionChanged: () => void;
};

export const useChatMuseStore = create<ChatMuseState>()(
  devtools(
    persist(
      (set) => ({
        seessionId: null,
        setSeessionId: (seessionId) =>
          set((state) =>
            state.seessionId === seessionId
              ? { seessionId }
              : {
                  seessionId,
                  entityCount: 0,
                  entityCountSource: null,
                  entityModalOpen: false,
                  entityModalSource: null,
                  entityFormModalOpen: false,
                  entityFormModalSource: null,
                },
          ),

        museType: null,
        setMuseType: (museType) => set({ museType }),

        chatIng: false,
        setChatIng: (chatIng) => set({ chatIng }),

        selectBrand: null,
        setSelectBrand: (selectBrand: BrandItem) => set({ selectBrand }),

        imageGlobaldisabled: false,
        setImageGlobaldisabled: (imageGlobaldisabled: boolean) => set({ imageGlobaldisabled }),

        entityCount: 0,
        entityCountSource: null,
        setEntityCount: (entityCount, source = 'session-query') => set({ entityCount, entityCountSource: source }),
        entityModalOpen: false,
        entityModalSource: null,
        setEntityModalOpen: (entityModalOpen, source) =>
          set({
            entityModalOpen,
            entityModalSource: entityModalOpen ? (source ?? 'entity') : null,
          }),
        entityFormModalOpen: false,
        entityFormModalSource: null,
        setEntityFormModalOpen: (entityFormModalOpen, source) =>
          set({
            entityFormModalOpen,
            entityFormModalSource: entityFormModalOpen ? (source ?? 'entity') : null,
          }),

        chatInputFileList: [],
        setChatInputFileList: (chatInputFileList) =>
          set((state) => ({
            chatInputFileList: typeof chatInputFileList === 'function' ? chatInputFileList(state.chatInputFileList) : chatInputFileList,
          })),

        resetPageStatus: () =>
          set({
            seessionId: null,
            selectBrand: null,
            chatInputFileList: [],
            chatIng: false,
            imageGlobaldisabled: false,
            museSelectedModel: undefined,
            museSelectedSize: undefined,
            museSelectedResolution: undefined,
            museActiveQuickTab: [],
            canvasId: null,
            museHistorySessionChanged: false,
            entityCount: 0,
            entityCountSource: null,
            entityModalOpen: false,
            entityModalSource: null,
            entityFormModalOpen: false,
            entityFormModalSource: null,
          }),

        modelList: {
          auto: [],
          image: [],
          reel: [],
        },
        setModelList: (modelList: MuseModelList) => set({ modelList }),
        museSelectedModel: undefined,
        setMuseSelectedModel: (museSelectedModel) => set({ museSelectedModel }),
        museSelectedSize: undefined,
        setMuseSelectedSize: (museSelectedSize) => set({ museSelectedSize }),
        museSelectedResolution: undefined,
        setMuseSelectedResolution: (museSelectedResolution) => set({ museSelectedResolution }),
        museActiveQuickTab: [],
        setMuseActiveQuickTab: (museActiveQuickTab) => set({ museActiveQuickTab }),
        canvasId: null,
        setCanvasId: (canvasId) => set({ canvasId }),
        museHistorySessionChanged: false,
        markMuseHistorySessionChanged: () => set({ museHistorySessionChanged: true }),
        resetMuseHistorySessionChanged: () => set({ museHistorySessionChanged: false }),
      }),
      {
        name: 'chat-muse-storage',
        partialize: (state) => ({
          museType: state.museType,
        }),
      },
    ),
    {
      name: 'ChatMuseStore',
    },
  ),
);
